/**
 * Referral qualification — the vocabulary behind "is this signup payable yet".
 *
 * A signup no longer credits the affiliate on registration. Every referral
 * lands as `Pending` and a nightly worker grades it against the active
 * qualification rule; only `Qualified` (the worker's verdict) and
 * `AdminApproved` (an operator's) are payable. Referrals that predate the
 * release were grandfathered as `Qualified` without ever being graded, which is
 * why `isGrandfathered` is reported separately.
 *
 * Payability is read from the row's own `isEligible`, never re-derived from the
 * status id: the API owns that rule, and a build that hard-codes "2 or 5" would
 * silently disagree with it the moment a sixth status exists.
 *
 * `criteriaMetMask` is the per-criterion verdict as bit flags. The flags are the
 * only thing this module interprets — the pass/fail of a criterion is the
 * server's, and the progress/threshold pairs on the row are rendered verbatim,
 * so no screen re-implements the rule.
 */
import type {
  AffiliateActivitySourceId,
  AffiliateQualificationStatus,
  AffiliateReferralCriterion,
  AffiliateReferralMetricSource,
} from './affiliateTypes';

export const AFFILIATE_QUALIFICATION_STATUS_IDS = {
  pending: 1,
  qualified: 2,
  rejected: 3,
  reversed: 4,
  adminApproved: 5,
} as const satisfies Record<AffiliateQualificationStatus, number>;

/** Every status, in the order a reviewer scans them. */
export const AFFILIATE_QUALIFICATION_STATUSES: readonly AffiliateQualificationStatus[] =
  ['pending', 'qualified', 'adminApproved', 'rejected', 'reversed'];

/**
 * What an operator can set a referral to. `qualified` is absent on purpose: 2
 * is the grading worker's verdict, and an operator marking a referral payable
 * by hand records `AdminApproved` so the decision stays attributable.
 */
export type AffiliateReferralOverrideDecision = Exclude<
  AffiliateQualificationStatus,
  'qualified'
>;

export const AFFILIATE_REFERRAL_OVERRIDE_DECISIONS: readonly AffiliateReferralOverrideDecision[] =
  ['adminApproved', 'rejected', 'reversed', 'pending'];

/**
 * The activity sources the rule can count towards `minActivityCount`, in the
 * order the settings screen lists them.
 *
 * Kept here rather than in the screen so the rule editor and any future
 * reporting of "what counts as a contribution" cannot drift apart or miss a
 * source the API added.
 */
export const AFFILIATE_ACTIVITY_SOURCE_IDS: readonly AffiliateActivitySourceId[] = [
  'countPosts',
  'countComments',
  'countPostReactions',
  'countPostShares',
  'countCommentReactions',
  'countGroupJoins',
];

const STATUS_BY_ID = new Map<number, AffiliateQualificationStatus>(
  (
    Object.entries(AFFILIATE_QUALIFICATION_STATUS_IDS) as [
      AffiliateQualificationStatus,
      number,
    ][]
  ).map(([status, id]) => [id, status]),
);

/** `qualificationStatus` as the API spells it; consulted only when the id is unknown. */
const STATUS_BY_NAME = new Map<string, AffiliateQualificationStatus>([
  ['pending', 'pending'],
  ['qualified', 'qualified'],
  ['rejected', 'rejected'],
  ['reversed', 'reversed'],
  ['adminapproved', 'adminApproved'],
  ['approved', 'adminApproved'],
]);

export interface AffiliateQualificationStatusSource {
  qualificationStatusId?: number | null;
  qualificationStatus?: string | null;
}

/**
 * The qualification state of a referral.
 *
 * Falls back to `pending` rather than to a verdict: an id this build does not
 * know must not be rendered as money the affiliate has earned, and "awaiting
 * qualification" is the one answer that is safe to be wrong about.
 */
export const affiliateQualificationStatus = (
  source: AffiliateQualificationStatusSource,
): AffiliateQualificationStatus => {
  if (typeof source.qualificationStatusId === 'number') {
    const byId = STATUS_BY_ID.get(source.qualificationStatusId);
    if (byId) return byId;
  }
  if (typeof source.qualificationStatus === 'string') {
    const byName = STATUS_BY_NAME.get(
      source.qualificationStatus.toLowerCase().replace(/[\s_-]/g, ''),
    );
    if (byName) return byName;
  }
  return 'pending';
};

export const affiliateQualificationStatusId = (
  status: AffiliateQualificationStatus,
): number => AFFILIATE_QUALIFICATION_STATUS_IDS[status];

/** `criteriaMetMask` bit flags, as published by the API. */
export const AFFILIATE_CRITERIA_FLAGS = {
  tenure: 1,
  activityCount: 2,
  activityDistinctDays: 4,
  activeLoginDays: 8,
  emailVerified: 16,
  goodStanding: 32,
} as const;

export type AffiliateCriterionId = keyof typeof AFFILIATE_CRITERIA_FLAGS;

/** Checklist order: the gates a referral clears, roughly in the order it clears them. */
export const AFFILIATE_CRITERION_IDS: readonly AffiliateCriterionId[] = [
  'tenure',
  'activityCount',
  'activityDistinctDays',
  'activeLoginDays',
  'emailVerified',
  'goodStanding',
];

/**
 * Whether the mask reports one criterion as met.
 *
 * The bitwise test is the API's wire format for `criteriaMetMask`, not an
 * optimisation, so it is the only faithful way to read it. It is confined to
 * this one helper; nothing else in the codebase touches the mask directly.
 *
 * There is deliberately no "all criteria met" helper. An all-met mask is not
 * the same thing as payable — a grandfathered referral is payable with nothing
 * met, and an operator override can make one payable against the mask — so a
 * screen must read the row's `isEligible` instead.
 */
export const affiliateCriterionMet = (
  criteriaMetMask: number,
  id: AffiliateCriterionId,
  // eslint-disable-next-line no-bitwise
): boolean => (criteriaMetMask & AFFILIATE_CRITERIA_FLAGS[id]) !== 0;

/**
 * Which metric and threshold field each counted criterion reads.
 *
 * `emailVerified` and `goodStanding` are absent: they are flags, not counts, so
 * they have no "6 / 14" to render and the mask bit is the whole verdict.
 */
const COUNTED_CRITERIA: Readonly<
  Partial<
    Record<
      AffiliateCriterionId,
      {
        value: keyof AffiliateReferralMetricSource;
        threshold: keyof AffiliateReferralMetricSource;
      }
    >
  >
> = {
  tenure: { value: 'tenureDays', threshold: 'minTenureDays' },
  activityCount: { value: 'activityCount', threshold: 'minActivityCount' },
  activityDistinctDays: {
    value: 'activityDistinctDays',
    threshold: 'minActivityDistinctDays',
  },
  activeLoginDays: {
    value: 'activeLoginDayCount',
    threshold: 'minActiveLoginDays',
  },
};

const metric = (value: number | null | undefined): number =>
  typeof value === 'number' && Number.isFinite(value) ? value : 0;

/**
 * The per-criterion checklist for one referral.
 *
 * `met` is the server's bit, not a comparison of the two numbers beside it: the
 * grading worker evaluated the referral under `appliedRuleId`, and the
 * thresholds on the row belong to the rule in force now. Re-deriving pass/fail
 * client-side would therefore contradict the verdict whenever the rule has
 * moved since the referral was graded.
 *
 * `required` says whether the active rule gates on the criterion at all, so a
 * threshold of 0 (or email verification switched off) reads as "not required"
 * instead of as a gate that passes for free.
 */
export const affiliateReferralCriteria = (
  source: AffiliateReferralMetricSource & { criteriaMetMask?: number | null },
): AffiliateReferralCriterion[] => {
  const mask = metric(source.criteriaMetMask);
  return AFFILIATE_CRITERION_IDS.map(id => {
    const counted = COUNTED_CRITERIA[id];
    if (counted) {
      const threshold = metric(source[counted.threshold] as number | null);
      return {
        id,
        met: affiliateCriterionMet(mask, id),
        value: metric(source[counted.value] as number | null),
        threshold,
        required: threshold > 0,
      };
    }
    return {
      id,
      met: affiliateCriterionMet(mask, id),
      value: null,
      threshold: null,
      // Good standing (not blocked, active, not deleted) is always enforced;
      // email verification is a switch on the rule.
      required: id === 'goodStanding' ? true : source.requireEmailVerified === true,
    };
  });
};
